import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import SignaturePad from 'signature_pad';

/**
 * Touch-optimized signature canvas using signature_pad.
 * Exposes getDataUrl() and isEmpty() via ref.
 */
const SignatureCanvas = forwardRef(function SignatureCanvas({ label }, ref) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgba(255,255,255,0)',
      penColor: '#1e293b',
      minWidth: 1,
      maxWidth: 3
    });
    padRef.current = pad;

    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      pad.clear();
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => {
      observer.disconnect();
      pad.off();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    getDataUrl: () => {
      if (!padRef.current || padRef.current.isEmpty()) return null;
      return padRef.current.toDataURL('image/png');
    },
    isEmpty: () => padRef.current?.isEmpty() ?? true,
    clear: () => padRef.current?.clear()
  }));

  return (
    <div className="signature-canvas-wrapper">
      {label && <p className="signature-label">{label}</p>}
      <canvas ref={canvasRef} className="signature-canvas" />
      <button
        type="button"
        className="signature-clear"
        onClick={() => padRef.current?.clear()}
      >
        Clear
      </button>
    </div>
  );
});

export default SignatureCanvas;
